import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { F, C, btn, inp, lbl } from '../../config/styles';
import { TERR, STANDARD_DEPTS, AVAILABLE_TRANSLATION_LANGS } from '../../config/constants';
import { FONT_OPTIONS, SYSTEM_DEFAULTS } from '../../config/tenantDefaults';
import { applyBranding, useTenantConfig } from '../../hooks/useTenant';
import { uid } from '../../utils/products';
import { FIELD_MAPPING_DEFAULTS, DEFAULT_REQUIRED_EXPORT_FIELDS, downloadCSV } from '../../utils/csv';
import { BUILTIN_FIELDS } from '../../config/fields';
import { parseMetafieldCsv, metafieldTemplateCsv } from '../../utils/metafieldImport';


const SECTIONS = [
  { id: 'general',         label: 'General' },
  { id: 'branding',        label: 'Branding' },
  { id: 'territories',     label: 'Territories' },
  { id: 'departments',     label: 'Departments' },
  { id: 'categories',      label: 'Calendar Categories' },
  { id: 'sizecategories',  label: 'Size Categories' },
  { id: 'producttypes',    label: 'Product Types' },
  { id: 'tags',            label: 'Tags & Badges' },
  { id: 'fields',          label: 'Fields' },
  { id: 'languages',       label: 'Translation Languages' },
  { id: 'variantpresets',  label: 'Variant Presets' },
  { id: 'croppresets',     label: 'Crop Presets' },
  { id: 'brandvoice',     label: 'Brand Voice' },
  { id: 'desctemplates',  label: 'Description Templates' },
  { id: 'integrations',   label: 'Integrations' },
];

// ─── Shared save state hook ───────────────────────────────────────────────────
function useSaveState() {
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');

  const run = async (fn) => {
    setSaving(true); setError(''); setSaved(false);
    const err = await fn();
    setSaving(false);
    if (err) { setError(err.message || String(err)); }
    else     { setSaved(true); setTimeout(() => setSaved(false), 2000); }
  };

  return { saving, saved, error, run };
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function SectionWrap({ title, desc, onSave, saving, saved, error, children }) {
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: F.display, fontSize: 20, color: C.text }}>{title}</div>
        {desc && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{desc}</div>}
      </div>
      {children}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 28, paddingTop: 20, borderTop: `1px solid ${C.e8}` }}>
        <button style={btn('primary')} onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved  && <span style={{ fontSize: 13, color: '#059669' }}>Saved</span>}
        {error  && <span style={{ fontSize: 13, color: '#dc2626' }}>{error}</span>}
      </div>
    </div>
  );
}

// ─── Colour picker swatch ─────────────────────────────────────────────────────
function ColorPick({ value, onChange }) {
  return (
    <input
      type="color"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ width: 40, height: 36, border: `1px solid ${C.e8}`, borderRadius: 6, cursor: 'pointer', padding: 2, boxSizing: 'border-box' }}
    />
  );
}

// ─── Inline string list editor ────────────────────────────────────────────────
function StringList({ items, setItems, placeholder, monoFont }) {
  const add    = ()       => setItems(prev => [...prev, '']);
  const remove = (i)      => setItems(prev => prev.filter((_, idx) => idx !== i));
  const update = (i, val) => setItems(prev => prev.map((v, idx) => idx === i ? val : v));

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        {items.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              style={{ ...inp(), fontSize: 12, fontFamily: monoFont ? F.mono : F.body, flex: 1, maxWidth: 320 }}
              value={v}
              placeholder={placeholder}
              onChange={e => update(i, e.target.value)}
            />
            <button
              onClick={() => remove(i)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: '0 4px' }}
            >×</button>
          </div>
        ))}
        {items.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>None added yet.</div>}
      </div>
      <button onClick={add} style={{ ...btn('secondary'), fontSize: 12 }}>+ Add</button>
    </div>
  );
}

// ─── Tenant list ──────────────────────────────────────────────────────────────
export function TenantAdminView() {
  const [tenants,       setTenants]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [editingTenant, setEditingTenant] = useState(null);
  const [newModal,      setNewModal]      = useState(false);

  const load = () => {
    setLoading(true);
    supabase.from('tenants').select('*').order('created_at')
      .then(({ data }) => { setTenants(data || []); setLoading(false); });
  };

  useEffect(load, []);

  if (editingTenant) {
    return (
      <TenantPreferences
        tenant={editingTenant}
        onBack={() => { setEditingTenant(null); load(); }}
        onUpdate={updated => setEditingTenant(updated)}
        platformAdmin
      />
    );
  }

  return (
    <div style={{ padding: '32px 40px', fontFamily: F.body, maxWidth: 820 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: F.display, fontSize: 22, color: C.text }}>Tenants</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>Manage client workspaces</div>
        </div>
        <button style={btn('primary')} onClick={() => setNewModal(true)}>+ New Tenant</button>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 14 }}>Loading…</div>
      ) : tenants.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 14 }}>No tenants yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tenants.map(t => {
            const deptCount = (t.config?.departments  || []).length;
            const terrCount = (t.config?.territories  || []).length;
            const catCount  = (t.config?.calCategories || []).length;
            return (
              <div
                key={t.id}
                style={{ background: C.white, border: `1px solid ${C.e8}`, borderRadius: 8, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 2 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: C.muted, fontFamily: F.mono }}>{t.subdomain}.range-builder.com</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                    {deptCount} dept{deptCount !== 1 ? 's' : ''} · {terrCount} territor{terrCount !== 1 ? 'ies' : 'y'} · {catCount} categor{catCount !== 1 ? 'ies' : 'y'}
                  </div>
                </div>
                <button style={{ ...btn('secondary'), fontSize: 13 }} onClick={() => setEditingTenant(t)}>
                  Settings
                </button>
              </div>
            );
          })}
        </div>
      )}

      {newModal && (
        <NewTenantModal onSave={() => { setNewModal(false); load(); }} onClose={() => setNewModal(false)} />
      )}
    </div>
  );
}

// ─── Preferences shell ────────────────────────────────────────────────────────
export function TenantPreferences({ tenant, onBack, onUpdate, platformAdmin = false, allowedSections }) {
  const visibleSections = allowedSections
    ? SECTIONS.filter(s => allowedSections.includes(s.id))
    : SECTIONS;
  const [activeSection, setActiveSection] = useState(visibleSections[0]?.id || 'general');
  const live = useTenantConfig();

  const saveConfig = async (partial) => {
    const newConfig = { ...(tenant.config || {}), ...partial };
    const { error } = await supabase.rpc('upsert_tenant', {
      p_subdomain: tenant.subdomain,
      p_slug:      tenant.slug || tenant.subdomain,
      p_name:      tenant.name,
      p_config:    newConfig,
    });
    if (!error) {
      onUpdate({ ...tenant, config: newConfig });
      // Editing the workspace you're in → apply it now, not on next page load.
      if (tenant.id && tenant.id === live?.tenantId) live.applyConfig?.(newConfig);
    }
    return error || null;
  };

  const saveName = async (name) => {
    const { error } = await supabase.rpc('upsert_tenant', {
      p_subdomain: tenant.subdomain,
      p_slug:      tenant.slug || tenant.subdomain,
      p_name:      name,
      p_config:    tenant.config || {},
    });
    if (!error) onUpdate({ ...tenant, name });
    return error || null;
  };

  return (
    <div style={{ display: 'flex', minHeight: '100%', fontFamily: F.body }}>
      {/* Sidebar nav */}
      <div style={{ width: 200, flexShrink: 0, borderRight: `1px solid ${C.e8}`, padding: '28px 0', background: C.fa }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: C.muted, padding: '0 20px', marginBottom: 20 }}
          >
            ← Tenants
          </button>
        )}
        <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 20px', marginBottom: 8 }}>
          {tenant.name}
        </div>
        {visibleSections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: activeSection === s.id ? C.white : 'none',
              border: 'none',
              borderLeft: activeSection === s.id ? `2px solid ${C.text}` : '2px solid transparent',
              cursor: 'pointer', fontSize: 13,
              color: activeSection === s.id ? C.text : C.text2,
              fontWeight: activeSection === s.id ? 500 : 400,
              padding: '8px 20px',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Section content — the Fields section gets a wider pane since its table has several
          columns (Label/Key/Export/Shopify-key/Type…) that need real room; every other section
          is prose/short-form and stays at the narrower width. */}
      <div style={{ flex: 1, padding: '32px 40px', overflowY: 'auto', maxWidth: activeSection === 'fields' ? 1040 : 680 }}>
        {activeSection === 'general'        && <GeneralSection        tenant={tenant}         saveName={saveName}    saveConfig={saveConfig} platformAdmin={platformAdmin} />}
        {activeSection === 'branding'       && <BrandingSection       config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'territories'    && <TerritoriesSection    config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'departments'    && <DepartmentsSection    config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'categories'     && <CategoriesSection     config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'sizecategories' && <SizeCategoriesSection config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'producttypes'   && <ProductTypesSection   config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'tags'           && <TagsSection           config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'fields'         && <FieldsSection         config={tenant.config}  saveConfig={saveConfig} subdomain={tenant.subdomain} />}
        {activeSection === 'languages'      && <LanguagesSection      config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'variantpresets'  && <VariantPresetsSection  config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'croppresets'     && <CropPresetsSection     config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'brandvoice'      && <BrandVoiceSection      config={tenant.config}  saveConfig={saveConfig} />}
        {activeSection === 'desctemplates'   && <DescriptionTemplatesSection config={tenant.config} saveConfig={saveConfig} />}
        {activeSection === 'integrations'    && <IntegrationsSection    config={tenant.config}  saveConfig={saveConfig} tenantId={tenant.id} />}
      </div>
    </div>
  );
}

// ─── Logo upload ─────────────────────────────────────────────────────────────
function LogoUpload({ logoUrl, setLogoUrl }) {
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(png|jpeg|webp)$/)) {
      setError('PNG, JPG or WebP only'); return;
    }
    setUploading(true); setError('');
    const ext  = file.name.split('.').pop();
    const path = `logos/${Date.now()}-logo.${ext}`;
    const { error: upErr } = await supabase.storage.from('tenant-assets').upload(path, file, { upsert: true });
    if (upErr) { setError(upErr.message); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('tenant-assets').getPublicUrl(path);
    setLogoUrl(publicUrl);
    setUploading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {logoUrl && (
          <div style={{ border: `1px solid ${C.e8}`, borderRadius: 8, padding: '8px 12px', background: C.fa, display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logoUrl} alt="Logo" style={{ height: 28, objectFit: 'contain', maxWidth: 160 }} />
            <button onClick={() => setLogoUrl('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ph, fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
          </div>
        )}
        <label style={{ ...btn('secondary'), cursor: 'pointer', fontSize: 12 }}>
          {uploading ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
          <input type="file" accept=".png,.jpg,.jpeg,.webp" style={{ display: 'none' }} onChange={handleFile} disabled={uploading} />
        </label>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>PNG or SVG with transparent background, displayed at 18px height.</div>
      {error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

// ─── Section: Branding ───────────────────────────────────────────────────────
function BrandingSection({ config, saveConfig }) {
  const b = config?.branding || {};
  const [appName,      setAppName]      = useState(b.appName      || '');
  const [primaryColor, setPrimaryColor] = useState(b.primaryColor || '#0a0a0a');
  const [primaryFg,    setPrimaryFg]    = useState(b.primaryFg    || '#ffffff');
  const [logoUrl,      setLogoUrl]      = useState(b.logoUrl      || '');
  const [displayFont,  setDisplayFont]  = useState(b.displayFont  || '');
  const { saving, saved, error, run }   = useSaveState();

  const preview = () => applyBranding({ appName, primaryColor, primaryFg, logoUrl, displayFont });

  return (
    <SectionWrap
      title="Branding"
      desc="Customise how your workspace looks."
      onSave={() => run(() => saveConfig({ branding: { appName, primaryColor, primaryFg, logoUrl, displayFont } }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div>
          <div style={lbl()}>App name</div>
          <input style={{ ...inp(), maxWidth: 320 }} value={appName} onChange={e => setAppName(e.target.value)} placeholder="Range Builder" />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Shown in the top bar. Leave blank to use your workspace name.</div>
        </div>

        <div>
          <div style={lbl()}>Logo</div>
          <LogoUpload logoUrl={logoUrl} setLogoUrl={setLogoUrl} />
        </div>

        <div>
          <div style={lbl()}>Display font</div>
          <select style={{ ...inp(), maxWidth: 240 }} value={displayFont} onChange={e => setDisplayFont(e.target.value)}>
            {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 24 }}>
          <div>
            <div style={lbl()}>Primary colour</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ColorPick value={primaryColor} onChange={setPrimaryColor} />
              <input style={{ ...inp(), width: 100, fontFamily: F.mono, fontSize: 12 }} value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Button background, active indicators.</div>
          </div>
          <div>
            <div style={lbl()}>Button text colour</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ColorPick value={primaryFg} onChange={setPrimaryFg} />
              <input style={{ ...inp(), width: 100, fontFamily: F.mono, fontSize: 12 }} value={primaryFg} onChange={e => setPrimaryFg(e.target.value)} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Text on primary buttons.</div>
          </div>
        </div>

        <div>
          <button onClick={preview} style={{ fontSize: 12, color: C.muted, background: 'none', border: `1px solid ${C.e8}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
            Preview changes
          </button>
          <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>Live preview, save to persist.</span>
        </div>

      </div>
    </SectionWrap>
  );
}

// ─── Section: General ─────────────────────────────────────────────────────────
function GeneralSection({ tenant, saveName, saveConfig, platformAdmin = false }) {
  const [name,       setName]       = useState(tenant.name);
  const [studioOnly, setStudioOnly] = useState(tenant.config?.studioOnly ?? false);
  const { saving, saved, error, run } = useSaveState();

  const handleSave = () => run(async () => {
    if (name.trim()) await saveName(name.trim());
    if (platformAdmin) await saveConfig({ studioOnly });
  });

  return (
    <SectionWrap title="General" onSave={handleSave} saving={saving} saved={saved} error={error}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={lbl()}>Display name</div>
          <input style={{ ...inp(), maxWidth: 320 }} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div>
          <div style={lbl()}>Subdomain</div>
          <div style={{ ...inp(), maxWidth: 320, background: C.fa, color: C.muted }}>{tenant.subdomain}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            {tenant.subdomain}.range-builder.com (cannot be changed after creation)
          </div>
        </div>
        {platformAdmin && (
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={studioOnly} onChange={e => setStudioOnly(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.text, fontFamily: F.display }}>Studio Portal mode</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Users on your workspace see only the Studio Portal: image upload, Dropbox/Drive import, and image CSV export. All other tabs are hidden.</div>
              </div>
            </label>
          </div>
        )}
      </div>
    </SectionWrap>
  );
}

// ─── Section: Territories ─────────────────────────────────────────────────────
function TerritoriesSection({ config, saveConfig }) {
  const standardCodes = new Set(TERR.map(t => t.code));
  const initial = config?.territories || [];
  const [selected, setSelected] = useState(initial.filter(t => standardCodes.has(t.code)).map(t => t.code));
  const [custom,   setCustom]   = useState(initial.filter(t => !standardCodes.has(t.code)).map(t => ({ ...t })));
  const { saving, saved, error, run } = useSaveState();

  const toggle = (code) => setSelected(prev =>
    prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
  );

  const addCustom    = ()            => setCustom(prev => [...prev, { code: '', name: '', currency: '', symbol: '', flag: '🏳️' }]);
  const removeCustom = (i)           => setCustom(prev => prev.filter((_, idx) => idx !== i));
  const updateCustom = (i, fld, val) => setCustom(prev => prev.map((t, idx) => idx === i ? { ...t, [fld]: val } : t));

  const buildTerritory = (t) => {
    const currency = t.currency.toUpperCase();
    return { code: t.code.toUpperCase(), name: t.name, currency, symbol: t.symbol, flag: t.flag || '🏳️', col: currency === 'GBP' ? null : currency };
  };

  const COLS = '60px 1fr 70px 60px 60px 28px';

  return (
    <SectionWrap
      title="Territories"
      desc="Active markets available to your workspace. Custom territories add a new currency column to the Price Table automatically."
      onSave={() => run(() => saveConfig({
        territories: [
          ...TERR.filter(t => selected.includes(t.code)),
          ...custom.filter(t => t.code.trim() && t.name.trim() && t.currency.trim()).map(buildTerritory),
        ],
      }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 20 }}>
        {TERR.map(t => (
          <label key={t.code} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '5px 0' }}>
            <input type="checkbox" checked={selected.includes(t.code)} onChange={() => toggle(t.code)} />
            <span style={{ fontSize: 13, color: C.text }}>{t.flag} {t.name}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{t.currency}</span>
          </label>
        ))}
      </div>

      <div style={{ borderTop: `1px solid ${C.e8}`, paddingTop: 16 }}>
        <div style={{ ...lbl(), marginBottom: 10 }}>Custom territories</div>
        {custom.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, marginBottom: 6 }}>
            {['Code', 'Name', 'Currency', 'Symbol', 'Flag', ''].map(h => (
              <div key={h} style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {custom.map((t, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center' }}>
              <input style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }} placeholder="BR" value={t.code}
                onChange={e => updateCustom(i, 'code', e.target.value.toUpperCase().replace(/\s/g, ''))} />
              <input style={inp()} placeholder="Brazil" value={t.name}
                onChange={e => updateCustom(i, 'name', e.target.value)} />
              <input style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }} placeholder="BRL" value={t.currency}
                onChange={e => updateCustom(i, 'currency', e.target.value.toUpperCase().replace(/\s/g, ''))} />
              <input style={inp()} placeholder="R$" value={t.symbol}
                onChange={e => updateCustom(i, 'symbol', e.target.value)} />
              <input style={inp()} placeholder="🇧🇷" value={t.flag}
                onChange={e => updateCustom(i, 'flag', e.target.value)} />
              <button onClick={() => removeCustom(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          ))}
          {custom.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No custom territories yet.</div>}
        </div>
        <button onClick={addCustom} style={{ ...btn('secondary'), fontSize: 12 }}>+ Add custom territory</button>
      </div>
    </SectionWrap>
  );
}

// ─── Section: Departments ─────────────────────────────────────────────────────
const CANON_DEPT = Object.fromEntries(STANDARD_DEPTS.map(d => [d.id, d]));
const isBuiltinDept = id => !!CANON_DEPT[id];

function DepartmentsSection({ config, saveConfig }) {
  // One ordered list of the workspace's sections. The six built-ins can be enabled/disabled and
  // renamed (fixed ids — they own the bespoke product tabs), but not deleted. Custom sections can
  // be added, renamed, reordered and removed; they're fields-only (their fields are assigned in
  // Settings → Fields and render via the generic form). Save order sets the product-editor tab order.
  const [rows, setRows] = useState(() => {
    const saved = config?.departments;
    if (saved && saved.length) {
      const present = saved.map(d => ({ id: d.id, label: d.label ?? '', desc: d.desc ?? '', custom: !isBuiltinDept(d.id), enabled: true }));
      const missingBuiltins = STANDARD_DEPTS.filter(d => !saved.some(s => s.id === d.id))
        .map(d => ({ id: d.id, label: d.label, desc: d.desc, custom: false, enabled: false }));
      return [...present, ...missingBuiltins];
    }
    return STANDARD_DEPTS.map(d => ({ id: d.id, label: d.label, desc: d.desc, custom: false, enabled: true }));
  });
  const { saving, saved, error, run } = useSaveState();

  const update = (i, field, val) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const move   = (i, dir) => setRows(prev => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const addCustom    = () => setRows(prev => [...prev, { id: uid(), label: '', desc: '', custom: true, enabled: true }]);
  const removeCustom = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

  const save = () => run(() => saveConfig({
    departments: rows
      .filter(r => r.custom || r.enabled)
      .map(r => ({
        id: r.id,
        label: r.label.trim() || (CANON_DEPT[r.id]?.label ?? 'Section'),
        desc: r.desc,
        ...(r.custom ? { custom: true } : {}),
      })),
  }));

  return (
    <SectionWrap
      title="Departments"
      desc="The sections of this workspace, in tab order. Rename or reorder any of them; add your own custom sections (their fields are assigned under Fields)."
      onSave={save}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((r, i) => {
          const on = r.custom || r.enabled;
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 4 }}>
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  style={{ ...btn('ghost', { fontSize: 10, padding: '2px 6px' }), opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === rows.length - 1}
                  style={{ ...btn('ghost', { fontSize: 10, padding: '2px 6px' }), opacity: i === rows.length - 1 ? 0.3 : 1 }}>↓</button>
              </div>
              {r.custom
                ? <button onClick={() => removeCustom(i)} title="Remove section"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: '8px 4px 0' }}>×</button>
                : <input type="checkbox" checked={r.enabled} onChange={() => update(i, 'enabled', !r.enabled)} style={{ marginTop: 10, cursor: 'pointer' }} />}
              <div style={{ flex: 1, opacity: on ? 1 : 0.5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <input
                    style={{ ...inp(), fontSize: 13, fontWeight: 500, flex: 1 }}
                    value={r.label}
                    disabled={!on}
                    placeholder={CANON_DEPT[r.id]?.label || 'Section name'}
                    onChange={e => update(i, 'label', e.target.value)}
                  />
                  {r.custom && <span style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Custom</span>}
                </div>
                <input
                  style={{ ...inp(), fontSize: 12, color: C.muted }}
                  value={r.desc}
                  disabled={!on}
                  placeholder={CANON_DEPT[r.id]?.desc || 'What this section is for'}
                  onChange={e => update(i, 'desc', e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </div>
      <button onClick={addCustom} style={{ ...btn('secondary'), fontSize: 12, marginTop: 14 }}>+ Add section</button>
    </SectionWrap>
  );
}

// ─── Section: Calendar Categories ────────────────────────────────────────────
function CategoriesSection({ config, saveConfig }) {
  const [cats, setCats] = useState((config?.calCategories || []).map(c => ({ ...c })));
  const { saving, saved, error, run } = useSaveState();

  const add    = ()            => setCats(prev => [...prev, { id: '', label: '', bg: '#f5f5f5', border: '#e5e5e5', fg: '#333333' }]);
  const remove = (i)           => setCats(prev => prev.filter((_, idx) => idx !== i));
  const update = (i, fld, val) => setCats(prev => prev.map((c, idx) => idx === i ? { ...c, [fld]: val } : c));

  const COLS = '130px 1fr 40px 40px 40px 28px';

  return (
    <SectionWrap
      title="Calendar Categories"
      desc="Product categories shown in the launch calendar. Each needs an ID, label, and three colours (background, border, text)."
      onSave={() => run(() => saveConfig({ calCategories: cats.filter(c => c.id.trim() && c.label.trim()) }))}
      saving={saving} saved={saved} error={error}
    >
      {cats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, marginBottom: 6 }}>
          {['ID', 'Label', 'BG', 'Border', 'Text', ''].map(h => (
            <div key={h} style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {cats.map((cat, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center' }}>
            <input
              style={{ ...inp(), fontSize: 12 }}
              placeholder="swim"
              value={cat.id}
              onChange={e => update(i, 'id', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
            />
            <input
              style={{ ...inp(), fontSize: 12 }}
              placeholder="Swim"
              value={cat.label}
              onChange={e => update(i, 'label', e.target.value)}
            />
            <ColorPick value={cat.bg}     onChange={v => update(i, 'bg', v)}     />
            <ColorPick value={cat.border} onChange={v => update(i, 'border', v)} />
            <ColorPick value={cat.fg}     onChange={v => update(i, 'fg', v)}     />
            <button
              onClick={() => remove(i)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}
            >×</button>
          </div>
        ))}
        {cats.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No categories yet.</div>}
      </div>
      <button onClick={add} style={{ ...btn('secondary'), fontSize: 12 }}>+ Add category</button>
    </SectionWrap>
  );
}

// ─── Section: Crop Presets ────────────────────────────────────────────────────
function CropPresetsSection({ config, saveConfig }) {
  // Unsaved tenants inherit the starter presets, so show those rather than an empty list.
  const [rows, setRows] = useState((config?.cropPresets ?? SYSTEM_DEFAULTS.cropPresets).map(r => ({ ...r })));
  const { saving, saved, error, run } = useSaveState();

  const add    = ()            => setRows(prev => [...prev, { id: `crop-${Date.now()}`, label: '', ratioW: 4, ratioH: 5, width: '' }]);
  const remove = (i)           => setRows(prev => prev.filter((_, idx) => idx !== i));
  const update = (i, fld, val) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [fld]: val } : r));

  const valid = r => r.label.trim() && Number(r.ratioW) > 0 && Number(r.ratioH) > 0;
  const COLS = '1fr 64px 12px 64px 100px 110px 28px';

  return (
    <SectionWrap
      title="Crop Presets"
      desc="Sizes offered by the crop tool in Studio, for images and video. Set a ratio, and optionally an output width in pixels (height follows the ratio). Leave the width blank to keep the cropped pixels as they are. Free-form crop is always available."
      onSave={() => run(() => saveConfig({ cropPresets: rows.filter(valid).map(r => ({ ...r, label: r.label.trim(), ratioW: Number(r.ratioW), ratioH: Number(r.ratioH), width: Number(r.width) > 0 ? Number(r.width) : '' })) }))}
      saving={saving} saved={saved} error={error}
    >
      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, marginBottom: 6 }}>
          {['Label', 'Ratio W', '', 'Ratio H', 'Width (px)', 'Output', ''].map((h, i) => (
            <div key={i} style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {rows.map((r, i) => {
          const w = Number(r.width), rw = Number(r.ratioW), rh = Number(r.ratioH);
          const outLabel = w > 0 && rw > 0 && rh > 0 ? `${w}×${Math.round(w * rh / rw)}` : 'as cropped';
          return (
            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 8, alignItems: 'center' }}>
              <input style={{ ...inp(), fontSize: 12 }} placeholder="Product page" value={r.label} onChange={e => update(i, 'label', e.target.value)} />
              <input style={{ ...inp(), fontSize: 12 }} type="number" min="1" value={r.ratioW} onChange={e => update(i, 'ratioW', e.target.value)} />
              <span style={{ fontSize: 12, color: C.muted, textAlign: 'center' }}>:</span>
              <input style={{ ...inp(), fontSize: 12 }} type="number" min="1" value={r.ratioH} onChange={e => update(i, 'ratioH', e.target.value)} />
              <input style={{ ...inp(), fontSize: 12 }} type="number" min="1" placeholder="optional" value={r.width} onChange={e => update(i, 'width', e.target.value)} />
              <span style={{ fontSize: 12, color: C.muted, fontFamily: F.mono }}>{outLabel}</span>
              <button onClick={() => remove(i)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          );
        })}
        {rows.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No presets — the crop tool will offer free-form only.</div>}
      </div>
      <button onClick={add} style={{ ...btn('secondary'), fontSize: 12 }}>+ Add preset</button>
    </SectionWrap>
  );
}

// ─── Section: Size Categories ─────────────────────────────────────────────────
function SizeCategoriesSection({ config, saveConfig }) {
  const normalizeCol = col => typeof col === 'string'
    ? { label: col, territories: [] }
    : { label: col.label || '', territories: [...(col.territories || [])] };

  const [cats, setCats] = useState((config?.sizeCategories || []).map(c => ({ ...c, cols: (c.cols || []).map(normalizeCol) })));
  const { saving, saved, error, run } = useSaveState();

  const add    = ()            => setCats(prev => [...prev, { id: '', label: '', subtitle: '', rowKey: '', cols: [] }]);
  const remove = (i)           => setCats(prev => prev.filter((_, idx) => idx !== i));
  const update = (i, fld, val) => setCats(prev => prev.map((c, idx) => idx === i ? { ...c, [fld]: val } : c));

  const addCol    = (i)                     => setCats(prev => prev.map((c, idx) => idx === i ? { ...c, cols: [...c.cols, { label: '', territories: [] }] } : c));
  const removeCol = (i, colIdx)             => setCats(prev => prev.map((c, idx) => idx === i ? { ...c, cols: c.cols.filter((_, ci) => ci !== colIdx) } : c));
  const updateCol = (i, colIdx, fld, val)   => setCats(prev => prev.map((c, idx) => idx === i ? { ...c, cols: c.cols.map((col, ci) => ci === colIdx ? { ...col, [fld]: val } : col) } : c));

  return (
    <SectionWrap
      title="Size Categories"
      desc="Sections in the Size Table view. Each category has an ID (data key), a label, a row key (first column header), and columns. Each column has a label plus the territory codes it covers, used to translate variant sizes on export."
      onSave={() => run(() => saveConfig({ sizeCategories: cats.filter(c => c.id.trim() && c.label.trim()) }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {cats.map((cat, i) => (
          <div key={i} style={{ background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 8, padding: '16px 18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <div style={lbl()}>ID</div>
                <input
                  style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }}
                  placeholder="e.g. footwear"
                  value={cat.id}
                  onChange={e => update(i, 'id', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                />
              </div>
              <div>
                <div style={lbl()}>Label</div>
                <input
                  style={inp()}
                  placeholder="e.g. Footwear Sizing"
                  value={cat.label}
                  onChange={e => update(i, 'label', e.target.value)}
                />
              </div>
              <div>
                <div style={lbl()}>Row key</div>
                <input
                  style={inp()}
                  placeholder="e.g. UK Shoe"
                  value={cat.rowKey}
                  onChange={e => update(i, 'rowKey', e.target.value)}
                />
              </div>
              <div>
                <div style={lbl()}>Subtitle</div>
                <input
                  style={inp()}
                  placeholder="Optional description"
                  value={cat.subtitle || ''}
                  onChange={e => update(i, 'subtitle', e.target.value)}
                />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={lbl()}>Columns</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(cat.cols || []).map((col, colIdx) => (
                  <div key={colIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 8, alignItems: 'center' }}>
                    <input
                      style={inp()}
                      placeholder="e.g. AUS"
                      value={col.label}
                      onChange={e => updateCol(i, colIdx, 'label', e.target.value)}
                    />
                    <input
                      key={`${i}-${colIdx}-territories`}
                      style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }}
                      placeholder="e.g. AU or DE,SE,DK,CH,EU"
                      defaultValue={(col.territories || []).join(', ')}
                      onBlur={e => updateCol(i, colIdx, 'territories', e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))}
                    />
                    <button
                      onClick={() => removeCol(i, colIdx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}
                    >×</button>
                  </div>
                ))}
                {(cat.cols || []).length === 0 && <div style={{ fontSize: 12, color: C.muted }}>No columns yet.</div>}
              </div>
              <button onClick={() => addCol(i)} style={{ ...btn('secondary'), fontSize: 11, padding: '4px 10px', marginTop: 8 }}>+ Add column</button>
            </div>
            <div style={{ textAlign: 'right' }}>
              <button
                onClick={() => remove(i)}
                style={{ ...btn('secondary'), fontSize: 12, color: '#dc2626', borderColor: '#fca5a5' }}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {cats.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No size categories configured. Without any, you'll fall back to the standard Bra / Clothes sections.</div>}
        <button style={{ ...btn('secondary'), fontSize: 12, alignSelf: 'flex-start' }} onClick={add}>+ Add category</button>
      </div>
    </SectionWrap>
  );
}

// ─── Section: Product Types ───────────────────────────────────────────────────
function ProductTypesSection({ config, saveConfig }) {
  const [types, setTypes] = useState([...(config?.productTypes || [])]);
  const { saving, saved, error, run } = useSaveState();

  return (
    <SectionWrap
      title="Product Types"
      desc="Options in the product type dropdown."
      onSave={() => run(() => saveConfig({ productTypes: types.map(t => t.trim()).filter(Boolean) }))}
      saving={saving} saved={saved} error={error}
    >
      <StringList items={types} setItems={setTypes} placeholder="e.g. T-Shirt" />
    </SectionWrap>
  );
}

// ─── Section: Tags & Badges ───────────────────────────────────────────────────
function TagsSection({ config, saveConfig }) {
  const [quickTags, setQuickTags] = useState([...(config?.quickTags || [])]);
  const [badges,    setBadges]    = useState([...(config?.badges    || [])]);
  const { saving, saved, error, run } = useSaveState();

  return (
    <SectionWrap
      title="Tags & Badges"
      desc="Quick-select options in the digital section."
      onSave={() => run(() => saveConfig({
        quickTags: quickTags.map(t => t.trim()).filter(Boolean),
        badges:    badges.map(b => b.trim()).filter(Boolean),
      }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <div>
          <div style={{ ...lbl(), marginBottom: 10 }}>Quick Tags</div>
          <StringList items={quickTags} setItems={setQuickTags} placeholder="collection-auto_..." monoFont />
        </div>
        <div>
          <div style={{ ...lbl(), marginBottom: 10 }}>Badges</div>
          <StringList items={badges} setItems={setBadges} placeholder="e.g. New In" />
        </div>
      </div>
    </SectionWrap>
  );
}


// ─── Section: Fields (unified) ────────────────────────────────────────────────
// One place to manage every field of every section: rename built-ins, delete out the ones you
// don't use, toggle Required / Scored, set the Shopify metafield key, and add/remove custom
// fields — grouped by department, each row collapsing to a name pill until expanded.
// Owns config.groupingField/groupingLabel, metafieldNamespace, disabledFields,
// exportRequiredFields, fieldMappings, fieldLabels, fieldScoring and metafields (custom fields).

const CUSTOM_IS_LONG   = t => t === 'longtext' || t === 'multi_line_text_field' || t === 'list.single_line_text_field';
const CUSTOM_TYPE_FOR  = (kind, long) => kind === 'standard' ? (long ? 'longtext' : 'text') : (long ? 'multi_line_text_field' : 'single_line_text_field');
// Shopify metafield types (metafield-kind fields). Native CSV columns only use Text / Long text.
const METAFIELD_TYPES = [
  { value: 'single_line_text_field',      label: 'Text' },
  { value: 'multi_line_text_field',       label: 'Long text' },
  { value: 'number_integer',              label: 'Integer' },
  { value: 'number_decimal',              label: 'Decimal' },
  { value: 'list.single_line_text_field', label: 'List' },
  { value: 'list.product_reference',      label: 'Products' },
  { value: 'list.page_reference',         label: 'Pages' },
];
const STANDARD_TYPES = [
  { value: 'text',     label: 'Text' },
  { value: 'longtext', label: 'Long text' },
];

// Friendly read-only labels for a built-in field's fixed type (custom fields pick theirs).
const TYPE_LABEL = {
  text: 'Text', longtext: 'Long text', richtext: 'Rich text', number: 'Number',
  price: 'Price', date: 'Date', select: 'Select', tags: 'Tags', image: 'Image',
};
// Built-ins are just a starting seed so the app isn't a blank shell — a tenant can delete out any
// of them exactly like a field they made themselves. The ONLY mandatory ones are the locked
// structural anchors (identity, price, image, variant SKU/size) that bespoke machinery stands on.
const canDeleteField = f => !f.locked;
// One shared grid so built-in and custom rows line up into a single consistent table.
const FIELDS_GRID = '1fr 96px 92px 1fr 84px 40px 40px 52px 26px';
// Fixed width for collapsed field pills and the "+ Add field" button, so they all line up.
const FIELD_ROW_W = 280;

function FieldsSection({ config, saveConfig, subdomain }) {
  const deptOptions = config?.departments?.length ? config.departments : STANDARD_DEPTS;

  const [groupingField, setGroupingField] = useState(config?.groupingField ?? 'titleNoColour');
  const [groupingLabel, setGroupingLabel] = useState(config?.groupingLabel || 'colour');
  const [metafieldNs,  setMetafieldNs]  = useState(config?.metafieldNamespace || '');
  const [disabled, setDisabled] = useState(config?.disabledFields ?? []);
  const [required, setRequired] = useState(config?.exportRequiredFields ?? DEFAULT_REQUIRED_EXPORT_FIELDS);
  const [mappings, setMappings] = useState({ ...(config?.fieldMappings || {}) });
  const [labels,   setLabels]   = useState({ ...(config?.fieldLabels || {}) });
  const [scoring,  setScoring]  = useState({ ...(config?.fieldScoring || {}) });
  const [custom,   setCustom]   = useState((config?.metafields || []).map(f => ({ ...f, _uid: f._uid || uid() })));
  const [expanded, setExpanded] = useState(() => new Set());   // ids of rows currently open for editing
  const importRef = useRef(null);
  const [importMsg, setImportMsg] = useState('');
  const { saving, saved, error, run } = useSaveState();

  const toggleExpand = (id) => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const nsDisplay = metafieldNs.trim() || subdomain || 'your-store';

  const toggle = (setter, key) => setter(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const setLabel = (key, val) => setLabels(prev => { const n = { ...prev }; if (val.trim()) n[key] = val; else delete n[key]; return n; });
  const setKey   = (key, val) => setMappings(prev => { const n = { ...prev }; if (val.trim()) n[key] = val; else delete n[key]; return n; });
  // Store a scored override only when it differs from the field's built-in default, so the config
  // stays clean and getFieldDefs' "override ?? default" logic keeps working.
  const setScored = (key, def, val) => setScoring(prev => { const n = { ...prev }; if (val === def) delete n[key]; else n[key] = val; return n; });
  const updCustom = (i, patch) => setCustom(prev => prev.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const addCustom = (dept) => { const _uid = uid(); setCustom(prev => [...prev, { key: '', label: '', dept, kind: 'metafield', type: 'single_line_text_field', shopifyKey: '', columnHeader: '', _uid }]); setExpanded(prev => new Set(prev).add(_uid)); };

  // Bulk-import metafield definitions from a CSV: merge by key / namespace.key
  // (update existing, append new), then the tenant reviews the rows and Saves as normal.
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';   // allow re-selecting the same file
    if (!file) return;
    let text;
    try { text = await file.text(); } catch (_) { setImportMsg('Could not read that file.'); return; }
    const { fields, errors } = parseMetafieldCsv(text, { deptIds: deptOptions.map(d => d.id) });
    if (!fields.length) { setImportMsg(errors[0] || 'No valid rows found in the CSV.'); return; }
    setCustom(prev => {
      const next = prev.map(f => ({ ...f }));
      let added = 0, updated = 0;
      for (const nf of fields) {
        const idx = next.findIndex(f =>
          (f.key && f.key === nf.key) ||
          (nf.shopifyKey && f.shopifyKey && f.shopifyKey.toLowerCase() === nf.shopifyKey.toLowerCase()));
        if (idx >= 0) { next[idx] = { ...next[idx], ...nf, _uid: next[idx]._uid }; updated++; }
        else { next.push({ ...nf, _uid: uid() }); added++; }
      }
      const parts = [];
      if (added)   parts.push(`${added} added`);
      if (updated) parts.push(`${updated} updated`);
      if (errors.length) parts.push(`${errors.length} skipped`);
      setImportMsg(`Imported ${parts.join(', ') || 'nothing'} — review below, then Save.`);
      return next;
    });
  };

  const save = () => run(() => saveConfig({
    groupingField, groupingLabel,
    metafieldNamespace: metafieldNs.trim(),
    disabledFields: disabled,
    exportRequiredFields: required,
    fieldMappings: mappings,
    fieldLabels: labels,
    fieldScoring: scoring,
    metafields: custom.filter(f => (f.key || '').trim() && (f.label || '').trim()).map(({ _uid, ...f }) => f),
  }));

  const customFields = config?.metafields || [];
  const groupOptions = [
    { value: '', label: 'Disabled — no grouping' },
    { value: 'titleNoColour', label: 'Title without colour (built-in)' },
    ...customFields.filter(f => (f.key || '').trim()).map(f => ({ value: f.key, label: f.label || f.key })),
  ];

  const th = { fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const chk = (checked, onChange, title) => (
    <input type="checkbox" checked={checked} onChange={onChange} title={title} style={{ cursor: 'pointer' }} />
  );

  return (
    <SectionWrap
      title="Fields"
      desc="Every field of every section in one table. Rename any field, delete out the ones you don't use, mark what's required to export or counts toward completion, set its Shopify key, and add your own fields."
      onSave={save}
      saving={saving} saved={saved} error={error}
    >
      {/* Grouping + metafield namespace */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <div style={lbl()}>Group products by</div>
          <select style={{ ...inp(), width: 280, height: 38 }} value={groupingField} onChange={e => setGroupingField(e.target.value)}>
            {groupOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {groupingField && (
          <div>
            <div style={lbl()}>Group label</div>
            <input style={{ ...inp(), width: 160, height: 38 }} value={groupingLabel} onChange={e => setGroupingLabel(e.target.value)} placeholder="colour" />
          </div>
        )}
        <div>
          <div style={lbl()}>Metafield namespace</div>
          <input style={{ ...inp(), width: 200, height: 38, fontFamily: F.mono }} value={metafieldNs}
            onChange={e => setMetafieldNs(e.target.value.replace(/\s/g, ''))} placeholder={subdomain || 'store'} />
          <div style={{ fontSize: 10, color: C.ph, marginTop: 3 }}>Default metafield namespace. Override per field with <code>namespace.key</code> (e.g. specifications.size).</div>
        </div>
      </div>

      {/* Bulk-import custom metafields from a CSV */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <input ref={importRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImport} />
        <button style={btn('secondary', { fontSize: 12, padding: '6px 12px' })} onClick={() => importRef.current?.click()}>Import metafields (CSV)</button>
        <button style={{ ...btn('ghost', { fontSize: 12, color: C.muted }) }} onClick={() => downloadCSV(metafieldTemplateCsv(), 'metafields-template.csv')}>Download template</button>
        {importMsg && <span style={{ fontSize: 12, color: C.muted }}>{importMsg}</span>}
      </div>

      {deptOptions.map(d => {
        const builtins = BUILTIN_FIELDS.filter(f => f.dept === d.id);
        const customs  = custom.map((f, i) => ({ f, i })).filter(x => (x.f.dept || 'digital') === d.id);
        const cell = { display: 'flex', alignItems: 'center', justifyContent: 'center' };
        // Read-only version of the row widgets — same shape as the editable ones, visibly disabled.
        const ro = { ...inp(), fontSize: 12, background: C.fa, color: C.muted, cursor: 'not-allowed' };
        const chevBtn = { background: 'none', border: 'none', cursor: 'pointer', color: C.ph, fontSize: 10, lineHeight: 1, padding: 0, flexShrink: 0 };

        // A completed field collapses to a compact name-only pill; clicking it opens the full editor.
        const collapsed = (id, label, action) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div onClick={() => toggleExpand(id)} title="Edit field"
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '7px 12px', border: `1px solid ${C.e8}`, borderRadius: 8, background: C.white, width: FIELD_ROW_W, boxSizing: 'border-box' }}>
              <span style={{ color: C.ph, fontSize: 9, flexShrink: 0 }}>▸</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label || <span style={{ color: C.ph, fontWeight: 400 }}>Untitled field</span>}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: 26, justifyContent: 'center' }}>{action}</div>
          </div>
        );

        const visBuiltins = builtins.filter(f => !disabled.includes(f.key));
        const anyOpen =
          visBuiltins.some(f => expanded.has(f.key)) ||
          customs.some(({ f }) => !((f.key || '').trim() && (f.label || '').trim()) || expanded.has(f._uid));

        return (
          <div key={d.id} style={{ borderTop: `1px solid ${C.e8}`, paddingTop: 16, marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>{d.label}</div>

            {/* Column headers only make sense when a row is expanded for editing */}
            {anyOpen && (
              <div style={{ display: 'grid', gridTemplateColumns: FIELDS_GRID, gap: 8, marginBottom: 6, alignItems: 'center' }}>
                {['Label', 'Key', 'Export as', 'Shopify key / column', 'Type', 'Var', 'Req', 'Scored', ''].map((h, i) =>
                  <div key={i} style={{ ...th, textAlign: i >= 5 && i <= 7 ? 'center' : 'left' }}>{h}</div>)}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Built-in fields are a starting seed. Deleting one removes it entirely (× → it's
                  gone from forms, scoring and export); a locked structural anchor keeps its 🔒. */}
              {visBuiltins.map(f => {
                const deletable = canDeleteField(f);
                const isMeta   = f.export?.kind === 'metafield';
                const isScored = scoring[f.key] !== undefined ? scoring[f.key] : !!f.scored;
                const isReq    = required.includes(f.key);
                const action = deletable
                  ? <button onClick={() => toggle(setDisabled, f.key)} title="Delete this field"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                  : <span style={{ color: C.ph, fontSize: 11 }} title="Structural field — always present">🔒</span>;

                if (!expanded.has(f.key)) return collapsed(f.key, labels[f.key] ?? f.label, action);

                // Expanded: same widgets as a custom row — the structurally-fixed cells (key, export
                // shape, native header, type, scope) are disabled so the built-in stays wired to its
                // exact Shopify contract; the editable cells (label, metafield key, req, scored) live.
                return (
                  <div key={f.key} style={{ display: 'grid', gridTemplateColumns: FIELDS_GRID, gap: 8, alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <button onClick={() => toggleExpand(f.key)} title="Collapse" style={chevBtn}>▾</button>
                      <input style={{ ...inp(), fontSize: 12, minWidth: 0 }} value={labels[f.key] ?? f.label} placeholder={f.label} onChange={e => setLabel(f.key, e.target.value)} />
                    </div>
                    <input style={ro} value={f.key} disabled title="Field key is fixed" />
                    <select style={ro} value={isMeta ? 'metafield' : 'standard'} disabled title="Export type is fixed for built-ins">
                      <option value="metafield">Metafield</option>
                      <option value="standard">CSV column</option>
                    </select>
                    {isMeta
                      ? <input style={{ ...inp(), fontSize: 11, fontFamily: F.mono }} value={mappings[f.key] || ''}
                          placeholder={`${nsDisplay}.${FIELD_MAPPING_DEFAULTS[f.key]}`} title="namespace.key — omit the namespace to use the default"
                          onChange={e => setKey(f.key, e.target.value.replace(/\s/g, ''))} />
                      : <input style={{ ...ro, fontFamily: F.mono, fontSize: 11 }} value={f.export?.header || '—'} disabled title="Shopify column is fixed" />}
                    <select style={ro} value={f.type} disabled title="Type is fixed for built-ins">
                      <option value={f.type}>{TYPE_LABEL[f.type] || f.type}</option>
                    </select>
                    <div style={cell}><input type="checkbox" checked={f.scope === 'variant'} disabled title="Scope is fixed for built-ins" style={{ cursor: 'not-allowed' }} /></div>
                    <div style={cell}>{f.requiredDefault ? chk(isReq, () => toggle(setRequired, f.key), 'Block export when empty') : ''}</div>
                    <div style={cell}>{chk(isScored, () => setScored(f.key, !!f.scored, !isScored), 'Count toward completion')}</div>
                    <div style={cell}>{action}</div>
                  </div>
                );
              })}

              {/* Custom fields for this section */}
              {customs.map(({ f, i }) => {
                const isStandard = f.kind === 'standard';
                const complete = !!((f.key || '').trim() && (f.label || '').trim());
                const open = !complete || expanded.has(f._uid);   // incomplete fields can't collapse
                const action = <button onClick={() => setCustom(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>;

                if (!open) return collapsed(f._uid, f.label, action);

                return (
                  <div key={f._uid} style={{ display: 'grid', gridTemplateColumns: FIELDS_GRID, gap: 8, alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {complete && <button onClick={() => toggleExpand(f._uid)} title="Collapse" style={chevBtn}>▾</button>}
                      <input style={{ ...inp(), fontSize: 12, minWidth: 0 }} placeholder="Label" value={f.label} onChange={e => updCustom(i, { label: e.target.value })} />
                    </div>
                    <input style={{ ...inp(), fontSize: 12, fontFamily: F.mono }} placeholder="key" value={f.key} onChange={e => updCustom(i, { key: e.target.value.replace(/\s/g, '') })} />
                    <select style={{ ...inp(), fontSize: 12 }} value={isStandard ? 'standard' : 'metafield'}
                      onChange={e => { const std = e.target.value === 'standard'; updCustom(i, { kind: std ? 'standard' : 'metafield', type: CUSTOM_TYPE_FOR(std ? 'standard' : 'metafield', CUSTOM_IS_LONG(f.type)) }); }}>
                      <option value="metafield">Metafield</option>
                      <option value="standard">CSV column</option>
                    </select>
                    {isStandard
                      ? <input style={{ ...inp(), fontSize: 12, fontFamily: F.mono }} placeholder="Column header" value={f.columnHeader || ''} onChange={e => updCustom(i, { columnHeader: e.target.value })} />
                      : <input style={{ ...inp(), fontSize: 11, fontFamily: F.mono }} placeholder={`${nsDisplay}.key`} title="namespace.key — omit the namespace to use the default"
                          value={f.shopifyKey || ''} onChange={e => updCustom(i, { shopifyKey: e.target.value.replace(/\s/g, '') })} />}
                    {isStandard
                      ? <select style={{ ...inp(), fontSize: 12 }} value={CUSTOM_IS_LONG(f.type) ? 'longtext' : 'text'}
                          onChange={e => updCustom(i, { type: e.target.value })}>
                          {STANDARD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      : <select style={{ ...inp(), fontSize: 12 }} value={f.type}
                          onChange={e => updCustom(i, { type: e.target.value })}>
                          {METAFIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>}
                    <div style={cell}>{chk(f.scope === 'variant', e => updCustom(i, { scope: e.target.checked ? 'variant' : 'product' }), 'Per-variant column (Ops grid)')}</div>
                    <div style={cell} />
                    <div style={cell}>{chk(!!f.scored, e => updCustom(i, { scored: e.target.checked }), 'Count toward completion')}</div>
                    <div style={cell}>{action}</div>
                  </div>
                );
              })}
            </div>

            {/* Removed built-in fields — restorable so a deletion is never permanent by mistake */}
            {builtins.some(f => disabled.includes(f.key)) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 11, color: C.muted }}>
                <span>Removed:</span>
                {builtins.filter(f => disabled.includes(f.key)).map(f => (
                  <button key={f.key} onClick={() => toggle(setDisabled, f.key)} title="Restore this field"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, color: C.text2, cursor: 'pointer' }}>
                    {labels[f.key] ?? f.label} <span style={{ fontSize: 12 }}>↺</span>
                  </button>
                ))}
              </div>
            )}

            <button onClick={() => addCustom(d.id)} style={{ ...btn('secondary'), fontSize: 12, marginTop: 8, width: FIELD_ROW_W, boxSizing: 'border-box' }}>+ Add field to {d.label}</button>
          </div>
        );
      })}
    </SectionWrap>
  );
}

// ─── Section: Translation Languages ──────────────────────────────────────────
function LanguagesSection({ config, saveConfig }) {
  const [selected, setSelected] = useState((config?.translationLangs || []).map(l => l.code));
  const { saving, saved, error, run } = useSaveState();

  const toggle = (code) => setSelected(prev =>
    prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
  );

  return (
    <SectionWrap
      title="Translation Languages"
      desc="Languages available for product content translation."
      onSave={() => run(() => saveConfig({ translationLangs: AVAILABLE_TRANSLATION_LANGS.filter(l => selected.includes(l.code)) }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {AVAILABLE_TRANSLATION_LANGS.map(l => (
          <label key={l.code} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '5px 0' }}>
            <input type="checkbox" checked={selected.includes(l.code)} onChange={() => toggle(l.code)} />
            <span style={{ fontSize: 13, color: C.text }}>{l.flag} {l.label}</span>
            <span style={{ fontSize: 11, color: C.muted, fontFamily: F.mono }}>{l.code}</span>
          </label>
        ))}
      </div>
    </SectionWrap>
  );
}

// ─── Section: Variant Presets ────────────────────────────────────────────────
function VariantPresetsSection({ config, saveConfig }) {
  const [presets, setPresets] = useState((config?.variantPresets || []).map(p => ({ ...p })));
  const { saving, saved, error, run } = useSaveState();

  const add = () => setPresets(prev => [...prev, {
    id: uid(), label: '', productTypes: [], opt1Name: 'Size', opt2Name: '',
    opt1Values: [], opt1Skus: [], opt2Values: [], opt2Skus: [],
  }]);

  const upd = (id, field, val) =>
    setPresets(prev => prev.map(p => p.id === id ? { ...p, [field]: val } : p));

  const remove = id => setPresets(prev => prev.filter(p => p.id !== id));

  const parseValues = str => str.split(',').map(s => s.trim()).filter(Boolean);

  return (
    <SectionWrap
      title="Variant Presets"
      desc="Size sets available when generating variants. Each preset adds a button to the Ops section."
      onSave={() => run(() => saveConfig({ variantPresets: presets }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {presets.map(p => (
          <div key={p.id} style={{ border: `1px solid ${C.e8}`, borderRadius: 8, padding: 14, background: C.fa }}>
            {/* Row 1: Label, product types, opt names */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <div style={lbl({ marginBottom: 4 })}>Label</div>
                <input style={inp()} value={p.label} onChange={e => upd(p.id, 'label', e.target.value)} placeholder="e.g. Leggings" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={lbl({ marginBottom: 4 })}>Product types <span style={{ color: C.ph, fontWeight: 400 }}>(leave blank for all)</span></div>
                <input style={inp()} value={(p.productTypes || []).join(', ')}
                  onChange={e => upd(p.id, 'productTypes', parseValues(e.target.value))}
                  placeholder="e.g. Top, Leggings" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={lbl({ marginBottom: 4 })}>Option 1 name</div>
                <input style={inp()} value={p.opt1Name} onChange={e => upd(p.id, 'opt1Name', e.target.value)} placeholder="Size" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={lbl({ marginBottom: 4 })}>Option 2 name <span style={{ color: C.ph, fontWeight: 400 }}>(optional)</span></div>
                <input style={inp()} value={p.opt2Name} onChange={e => upd(p.id, 'opt2Name', e.target.value)} placeholder="e.g. Colour" />
              </div>
              <button onClick={() => remove(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20, padding: '6px 4px', lineHeight: 1, marginBottom: 2 }}>×</button>
            </div>
            {/* Row 2: Values + SKU suffixes */}
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={lbl({ marginBottom: 4 })}>{p.opt2Name ? (p.opt1Name || 'Option 1') + ' labels' : 'Size labels'}</div>
                <input
                  key={p.id + '-opt1'}
                  style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }}
                  defaultValue={p.opt1Values.join(', ')}
                  onBlur={e => upd(p.id, 'opt1Values', parseValues(e.target.value))}
                  placeholder="XS, S, M, L, XL"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={lbl({ marginBottom: 4 })}>SKU suffixes <span style={{ color: C.ph, fontWeight: 400 }}>(if different from labels)</span></div>
                <input
                  key={p.id + '-opt1sku'}
                  style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }}
                  defaultValue={(p.opt1Skus || []).join(', ')}
                  onBlur={e => upd(p.id, 'opt1Skus', parseValues(e.target.value))}
                  placeholder="0, 1, 2, 3, 4"
                />
              </div>
              {p.opt2Name && (<>
                <div style={{ flex: 1 }}>
                  <div style={lbl({ marginBottom: 4 })}>{p.opt2Name} labels</div>
                  <input
                    key={p.id + '-opt2'}
                    style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }}
                    defaultValue={p.opt2Values.join(', ')}
                    onBlur={e => upd(p.id, 'opt2Values', parseValues(e.target.value))}
                    placeholder="Black, White, Grey"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={lbl({ marginBottom: 4 })}>{p.opt2Name} SKU suffixes</div>
                  <input
                    key={p.id + '-opt2sku'}
                    style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }}
                    defaultValue={(p.opt2Skus || []).join(', ')}
                    onBlur={e => upd(p.id, 'opt2Skus', parseValues(e.target.value))}
                    placeholder="optional"
                  />
                </div>
              </>)}
            </div>
            {/* Preview */}
            {p.opt1Values.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 11, color: C.muted, fontFamily: F.mono }}>
                Preview: {p.opt1Values.map((v, i) => {
                  const sku = p.opt1Skus?.[i] || v;
                  return sku === v ? v : `${v}→${sku}`;
                }).join(', ')}
              </div>
            )}
          </div>
        ))}
        {presets.length === 0 && (
          <div style={{ fontSize: 13, color: C.muted }}>
            No presets configured. Users will see a prompt to add them here.
          </div>
        )}
      </div>
      <button onClick={add} style={{ ...btn('secondary'), fontSize: 12, marginTop: 12 }}>+ Add preset</button>
    </SectionWrap>
  );
}

// ─── Section: Brand Voice ─────────────────────────────────────────────────────
const BLANK_BRAND_VOICE = () => ({ id: uid(), brandName: '', toneOfVoice: '', styleNotes: '', avoid: '', exampleDescription: '' });

function BrandVoiceSection({ config, saveConfig }) {
  const raw = config?.brandGuidelines;
  const initialBrands = Array.isArray(raw)
    ? (raw.length ? raw : [BLANK_BRAND_VOICE()])
    : (raw && Object.keys(raw).length ? [{ ...BLANK_BRAND_VOICE(), ...raw }] : [BLANK_BRAND_VOICE()]);

  const [brands,   setBrands]   = useState(initialBrands);
  const [activeId, setActiveId] = useState(initialBrands[0].id);
  const { saving, saved, error, run } = useSaveState();

  const active = brands.find(b => b.id === activeId) || brands[0];
  const updateActive = (patch) => setBrands(prev => prev.map(b => b.id === active.id ? { ...b, ...patch } : b));

  const addBrand = () => {
    const nb = BLANK_BRAND_VOICE();
    setBrands(prev => [...prev, nb]);
    setActiveId(nb.id);
  };

  const removeBrand = (id) => {
    if (brands.length <= 1) return;
    const next = brands.filter(b => b.id !== id);
    setBrands(next);
    if (activeId === id) setActiveId(next[0].id);
  };

  const taStyle = { ...inp(), fontFamily: F.mono, fontSize: 12, resize: 'vertical' };

  return (
    <SectionWrap
      title="Brand Voice"
      desc="Guidelines used by the AI description generator in the Creative section. The more detail you add, the better the output."
      onSave={() => run(() => saveConfig({ brandGuidelines: brands }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {brands.map(b => (
          <button
            key={b.id}
            onClick={() => setActiveId(b.id)}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              border: `1px solid ${b.id === active.id ? C.text : C.e8}`,
              background: b.id === active.id ? C.text : C.white,
              color: b.id === active.id ? C.white : C.text2,
            }}
          >
            {b.brandName || 'Untitled brand'}
          </button>
        ))}
        <button onClick={addBrand} style={{ ...btn('secondary'), fontSize: 12 }}>+ Add brand</button>
      </div>
      {brands.length > 1 && (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 20 }}>
          One profile per sub-brand. The AI matches each collection's title prefix against the brand name below (case-insensitive). For example, a collection titled <strong>Aurora: Spring Drop</strong> uses the “Aurora” voice. Just lead the collection title with the brand name; collections with no match use the first brand.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: brands.length > 1 ? 0 : 20 }}>

        <div>
          <div style={lbl()}>Brand name</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 320 }}>
            <input style={{ ...inp(), flex: 1 }} value={active.brandName} onChange={e => updateActive({ brandName: e.target.value })}
              placeholder="e.g. White Fox Boutique" />
            {brands.length > 1 && (
              <button onClick={() => removeBrand(active.id)} title="Remove this brand"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Used in the AI prompt header. Defaults to your workspace name if blank.</div>
        </div>

        <div>
          <div style={lbl()}>Tone of voice</div>
          <textarea style={{ ...taStyle, minHeight: 72 }} value={active.toneOfVoice} onChange={e => updateActive({ toneOfVoice: e.target.value })}
            placeholder="e.g. Aspirational, confident and inclusive, never salesy or over-formal. Speak to the customer like a trusted friend." />
        </div>

        <div>
          <div style={lbl()}>Style notes</div>
          <textarea style={{ ...taStyle, minHeight: 72 }} value={active.styleNotes} onChange={e => updateActive({ styleNotes: e.target.value })}
            placeholder="e.g. 2–3 short paragraphs using <p> tags. Keep sentences punchy. Lead with the occasion or feeling, then describe the product." />
        </div>

        <div>
          <div style={lbl()}>Avoid</div>
          <textarea style={{ ...taStyle, minHeight: 60 }} value={active.avoid} onChange={e => updateActive({ avoid: e.target.value })}
            placeholder="e.g. Don't mention price or discounts. Avoid 'perfect', 'amazing', 'incredible'. No passive voice." />
        </div>

        <div>
          <div style={lbl()}>Example description <span style={{ fontWeight: 400, color: C.muted }}>(HTML, used as a few-shot reference)</span></div>
          <textarea style={{ ...taStyle, minHeight: 140 }} value={active.exampleDescription} onChange={e => updateActive({ exampleDescription: e.target.value })}
            placeholder="<p>Paste a real product description here that represents the brand voice perfectly. The AI will match this quality and style.</p>" />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            The single most impactful field: paste an exemplary description and the AI will mirror it closely.
          </div>
        </div>

      </div>
    </SectionWrap>
  );
}

// ─── Section: Description Templates ──────────────────────────────────────────
function DescriptionTemplatesSection({ config, saveConfig }) {
  const [templates, setTemplates] = useState(
    (config?.descriptionTemplates ?? SYSTEM_DEFAULTS.descriptionTemplates).map(t => ({ ...t }))
  );
  const { saving, saved, error, run } = useSaveState();

  const add    = ()            => setTemplates(prev => [...prev, { id: uid(), label: '', body: '' }]);
  const remove = (i)           => setTemplates(prev => prev.filter((_, idx) => idx !== i));
  const update = (i, fld, val) => setTemplates(prev => prev.map((t, idx) => idx === i ? { ...t, [fld]: val } : t));

  return (
    <SectionWrap
      title="Description Templates"
      desc="Quick-insert buttons shown above the Description field in the Creative tab. Add one per product style you sell."
      onSave={() => run(() => saveConfig({ descriptionTemplates: templates.filter(t => t.label.trim()) }))}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
        {templates.map((t, i) => (
          <div key={t.id || i} style={{ border: `1px solid ${C.e8}`, borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input
                style={{ ...inp(), fontSize: 13, flex: 1 }}
                placeholder="Button label, e.g. Basic Tee"
                value={t.label}
                onChange={e => update(i, 'label', e.target.value)}
              />
              <button
                onClick={() => remove(i)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 18, lineHeight: 1, padding: 0 }}
              >×</button>
            </div>
            <textarea
              style={{ ...inp(), fontFamily: F.mono, fontSize: 12, minHeight: 100, resize: 'vertical' }}
              placeholder="<p>Template HTML inserted when this button is clicked…</p>"
              value={t.body}
              onChange={e => update(i, 'body', e.target.value)}
            />
          </div>
        ))}
        {templates.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>No templates yet.</div>}
      </div>
      <button onClick={add} style={{ ...btn('secondary'), fontSize: 12 }}>+ Add template</button>
    </SectionWrap>
  );
}

// ─── New tenant modal ─────────────────────────────────────────────────────────
function NewTenantModal({ onSave, onClose }) {
  const [subdomain, setSubdomain] = useState('');
  const [name,      setName]      = useState('');
  const { saving, error, run } = useSaveState();

  const save = () => {
    if (!subdomain.trim() || !name.trim()) return;
    run(async () => {
      const { error: err } = await supabase.rpc('upsert_tenant', {
        p_subdomain: subdomain,
        p_slug:      subdomain,
        p_name:      name.trim(),
        p_config:    {},
      });
      if (!err) onSave();
      return err || null;
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 12, padding: 28, width: 420, fontFamily: F.body }}>
        <div style={{ fontFamily: F.display, fontSize: 18, color: C.text, marginBottom: 24 }}>New Tenant</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={lbl()}>Subdomain</div>
            <input
              style={inp()}
              value={subdomain}
              onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="brandname"
            />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {subdomain || 'brandname'}.range-builder.com
            </div>
          </div>
          <div>
            <div style={lbl()}>Name</div>
            <input style={inp()} value={name} onChange={e => setName(e.target.value)} placeholder="Brand Name" />
          </div>
        </div>
        {error && <div style={{ fontSize: 13, color: '#dc2626', marginTop: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button style={btn('secondary')} onClick={onClose}>Cancel</button>
          <button style={btn('primary')} onClick={save} disabled={saving || !subdomain || !name}>
            {saving ? 'Creating…' : 'Create tenant'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Integrations ────────────────────────────────────────────────────
function IntegrationsSection({ config, saveConfig, tenantId }) {
  const [dropboxToken,  setDropboxToken]  = useState('');
  const [gdriveJson,    setGdriveJson]    = useState('');
  const { saving, saved, error, run } = useSaveState();

  const handleSave = () => run(async () => {
    const dbxToken = dropboxToken.trim();
    if (dbxToken) {
      const { error: e1 } = await supabase.rpc('upsert_tenant_secret', { p_key: 'dropboxAccessToken', p_value: dbxToken });
      if (e1) throw new Error(e1.message);
    }
    const gdJson = gdriveJson.trim();
    if (gdJson) {
      try { JSON.parse(gdJson); } catch { throw new Error('Google service account key is not valid JSON'); }
      const { error: e2 } = await supabase.rpc('upsert_tenant_secret', { p_key: 'googleServiceAccountJson', p_value: gdJson });
      if (e2) throw new Error(e2.message);
    }
    await saveConfig({
      ...(dbxToken  ? { dropboxEnabled: true }     : {}),
      ...(gdJson    ? { googleDriveEnabled: true }  : {}),
    });
    setDropboxToken('');
    setGdriveJson('');
  });

  return (
    <SectionWrap
      title="Integrations"
      desc="Third-party integrations used by this workspace."
      onSave={handleSave}
      saving={saving} saved={saved} error={error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* Dropbox */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8, fontFamily: F.display }}>
            Dropbox
            {config?.dropboxEnabled && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 400, marginLeft: 8 }}>✓ enabled</span>}
          </div>
          <div style={lbl()}>Access Token</div>
          <input
            type="password"
            style={{ ...inp(), maxWidth: 400, fontFamily: F.mono, fontSize: 12 }}
            value={dropboxToken}
            onChange={e => setDropboxToken(e.target.value)}
            placeholder={config?.dropboxEnabled ? '••••••••  (saved, paste to replace)' : 'Paste access token from Dropbox developer console'}
            autoComplete="off"
          />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            dropbox.com/developers → your app → OAuth 2 → Generated access token. Stored securely, never exposed to the browser.
          </div>
        </div>

        {/* Google Drive */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8, fontFamily: F.display }}>
            Google Drive
            {config?.googleDriveEnabled && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 400, marginLeft: 8 }}>✓ enabled</span>}
          </div>
          <div style={lbl()}>Service Account Key (JSON)</div>
          <textarea
            style={{ ...inp(), width: '100%', maxWidth: 480, fontFamily: F.mono, fontSize: 11, height: 90, resize: 'vertical' }}
            value={gdriveJson}
            onChange={e => setGdriveJson(e.target.value)}
            placeholder={config?.googleDriveEnabled ? '••••••••  (saved, paste new JSON to replace)' : 'Paste the contents of your service account key JSON file'}
            autoComplete="off"
            spellCheck={false}
          />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            Google Cloud Console → IAM → Service Accounts → Keys → Add Key → JSON.
            Add the service account email as a Viewer on your Shared Drive.
            Stored securely, never exposed to the browser.
          </div>
        </div>

      </div>
    </SectionWrap>
  );
}
